import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, API } from "@/lib/rn3";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, BarChart3, FileSpreadsheet, Wallet, FileText, Download, TrendingUp, Loader2, CreditCard, CheckCircle2 } from "lucide-react";

const fmt = (n) => { const v = Number(n || 0); const s = v < 0 ? "-" : ""; return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const firstOfYearISO = () => `${new Date().getFullYear()}-01-01`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const KPI = ({ label, value, accent = "" }) => (
  <div className="border border-[#E5E5E5] p-4 bg-white">
    <div className="rn3-eyebrow text-[10px]">{label}</div>
    <div className={`font-serif-display text-2xl mt-2 tabular-nums ${accent}`}>{value}</div>
  </div>
);

const downloadAuth = async (path, filename) => {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 4000);
  } catch { toast.error("Download failed"); }
};

const LineList = ({ lines, fallback }) => (
  <div className="space-y-1.5">
    {(!lines || lines.length === 0) && <div className="text-sm text-[#737373] italic">{fallback}</div>}
    {(lines || []).map((l, i) => (
      <div key={i} className="flex justify-between text-sm border-b border-[#F3EFE9] py-1.5">
        <span className="text-[#404040]">{l.name || l.account_name || l.code}</span>
        <span className="tabular-nums">{fmt(l.amount ?? l.balance ?? 0)}</span>
      </div>
    ))}
  </div>
);

const PLView = ({ data, lang }) => {
  if (!data || !data.totals || !data.sections) return null;
  return (
  <div className="space-y-6" data-testid="portal-pl-view">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPI label={lang === "es" ? "Ingresos" : "Total Income"} value={fmt(data.totals.income)} accent="text-[#15803D]" />
      <KPI label="COGS" value={fmt(data.totals.cogs)} />
      <KPI label={lang === "es" ? "Utilidad Bruta" : "Gross Profit"} value={fmt(data.totals.gross_profit)} />
      <KPI label={lang === "es" ? "Utilidad Neta" : "Net Income"} value={fmt(data.totals.net_income)} accent={data.totals.net_income >= 0 ? "text-[#15803D]" : "text-[#8B1C22]"} />
    </div>
    <div className="grid md:grid-cols-3 gap-6">
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Ingresos" : "Income"}</div><LineList lines={data.sections.income} fallback="—" /></section>
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">COGS</div><LineList lines={data.sections.cogs} fallback="—" /></section>
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Gastos" : "Expenses"}</div><LineList lines={data.sections.expense} fallback="—" /></section>
    </div>
  </div>
  );
};

const BSView = ({ data, lang }) => {
  if (!data || !data.totals || !data.sections) return null;
  return (
  <div className="space-y-6" data-testid="portal-bs-view">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <KPI label={lang === "es" ? "Activos" : "Assets"} value={fmt(data.totals.assets)} />
      <KPI label={lang === "es" ? "Pasivos" : "Liabilities"} value={fmt(data.totals.liabilities)} />
      <KPI label={lang === "es" ? "Capital" : "Equity"} value={fmt(data.totals.equity)} />
    </div>
    <div className="grid md:grid-cols-2 gap-6">
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Activos" : "Assets"}</div><LineList lines={data.sections.asset} fallback="—" /></section>
      <div className="space-y-6">
        <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Pasivos" : "Liabilities"}</div><LineList lines={data.sections.liability} fallback="—" /></section>
        <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Capital" : "Equity"}</div><LineList lines={data.sections.equity} fallback="—" /></section>
      </div>
    </div>
  </div>
  );
};

const CFView = ({ data, lang }) => {
  if (!data || !data.totals) return null;
  return (
  <div className="space-y-6" data-testid="portal-cf-view">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPI label={lang === "es" ? "Efectivo Inicial" : "Beginning Cash"} value={fmt(data.totals.beginning_cash)} />
      <KPI label={lang === "es" ? "Entradas" : "Inflows"} value={fmt(data.totals.total_inflow)} accent="text-[#15803D]" />
      <KPI label={lang === "es" ? "Salidas" : "Outflows"} value={fmt(data.totals.total_outflow)} accent="text-[#8B1C22]" />
      <KPI label={lang === "es" ? "Efectivo Final" : "Ending Cash"} value={fmt(data.totals.ending_cash)} />
    </div>
    <div className="grid md:grid-cols-2 gap-6">
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Entradas" : "Inflows"}</div><LineList lines={data.inflows} fallback="—" /></section>
      <section className="rn3-card"><div className="rn3-eyebrow mb-3">{lang === "es" ? "Salidas" : "Outflows"}</div><LineList lines={data.outflows} fallback="—" /></section>
    </div>
  </div>
  );
};

const InvoicesView = ({ bookId, lang, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/portal/books/${bookId}/invoices`); setItems(data); }
    finally { setLoading(false); }
  }, [bookId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const startCheckout = async (inv) => {
    setPaying(inv.id);
    try {
      const { data } = await api.post(`/portal/books/${bookId}/invoices/${inv.id}/checkout`, { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not create checkout session");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed to start");
    } finally { setPaying(null); }
  };

  if (loading) return <div className="py-16 text-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>;
  return (
    <div className="border border-[#E5E5E5] overflow-x-auto" data-testid="portal-invoices-view">
      <table className="w-full text-sm min-w-[760px]">
        <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
          <tr>
            <th className="py-3 px-3">#</th>
            <th className="py-3 px-3">{lang === "es" ? "Fecha" : "Date"}</th>
            <th className="py-3 px-3">{lang === "es" ? "Vence" : "Due"}</th>
            <th className="py-3 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
            <th className="py-3 px-3 text-right">{lang === "es" ? "Saldo" : "Balance"}</th>
            <th className="py-3 px-3">{lang === "es" ? "Estado" : "Status"}</th>
            <th className="py-3 px-3 text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="7" className="py-12 text-center text-[#737373]">{lang === "es" ? "Aún no hay facturas." : "No invoices yet."}</td></tr>}
          {items.map((inv) => {
            const canPay = inv.balance > 0.005 && inv.status !== "cancelled";
            return (
              <tr key={inv.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`portal-inv-row-${inv.id}`}>
                <td className="py-2 px-3 font-medium">{inv.invoice_number}</td>
                <td className="py-2 px-3 text-[#404040]">{inv.date}</td>
                <td className="py-2 px-3 text-[#404040]">{inv.due_date}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(inv.total)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${inv.balance > 0 ? "text-[#8B1C22]" : ""}`}>{fmt(inv.balance)}</td>
                <td className="py-2 px-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#C4A252] text-[#8B1C22]">{inv.status}</span></td>
                <td className="py-2 px-3 text-right space-x-3 whitespace-nowrap">
                  {canPay && (
                    <button
                      onClick={() => startCheckout(inv)}
                      disabled={paying === inv.id}
                      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1 bg-[#8B1C22] text-white hover:bg-[#6F1419] disabled:opacity-60"
                      data-testid={`portal-inv-pay-${inv.id}`}
                    >
                      {paying === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                      {lang === "es" ? "Pagar" : "Pay"}
                    </button>
                  )}
                  <button
                    onClick={() => downloadAuth(`/portal/books/${bookId}/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number}.pdf`)}
                    className="text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                    data-testid={`portal-inv-pdf-${inv.id}`}
                  ><Download className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ARView = ({ bookId, lang }) => {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get(`/portal/books/${bookId}/ar-aging`); setData(data); })(); }, [bookId]);
  if (!data) return <div className="py-16 text-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>;
  return (
    <div className="space-y-6" data-testid="portal-ar-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label={lang === "es" ? "Actual" : "Current"} value={fmt(data.buckets.current)} />
        <KPI label="1–30" value={fmt(data.buckets["1_30"])} />
        <KPI label="31–60" value={fmt(data.buckets["31_60"])} accent={data.buckets["31_60"] > 0 ? "text-[#C4A252]" : ""} />
        <KPI label="61–90" value={fmt(data.buckets["61_90"])} accent={data.buckets["61_90"] > 0 ? "text-[#8B1C22]" : ""} />
        <KPI label="90+" value={fmt(data.buckets.over_90)} accent={data.buckets.over_90 > 0 ? "text-[#8B1C22]" : ""} />
      </div>
      <div className="rn3-card">
        <div className="flex items-center justify-between mb-4">
          <div className="rn3-eyebrow">{lang === "es" ? "Facturas Abiertas" : "Open Invoices"}</div>
          <div className="font-serif-display text-xl tabular-nums">{fmt(data.total_open)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr><th className="py-2">#</th><th className="py-2">{lang === "es" ? "Vence" : "Due"}</th><th className="py-2 text-right">{lang === "es" ? "Total" : "Total"}</th><th className="py-2 text-right">{lang === "es" ? "Saldo" : "Balance"}</th><th className="py-2 text-right">{lang === "es" ? "Días" : "Days"}</th></tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-[#737373]">{lang === "es" ? "Nada pendiente." : "Nothing outstanding."}</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.invoice_id} className="border-b border-[#F3EFE9]">
                  <td className="py-2 font-medium">{r.invoice_number}</td>
                  <td className="py-2 text-[#404040]">{r.due_date}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(r.total)}</td>
                  <td className="py-2 text-right tabular-nums text-[#8B1C22]">{fmt(r.balance)}</td>
                  <td className={`py-2 text-right tabular-nums text-xs ${r.days_overdue > 0 ? "text-[#8B1C22] font-medium" : "text-[#737373]"}`}>{r.days_overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ClientReports = () => {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState("");
  const [tab, setTab] = useState("pl");
  const [start, setStart] = useState(firstOfYearISO());
  const [end, setEnd] = useState(todayISO());
  const [asOf, setAsOf] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payingBanner, setPayingBanner] = useState(null); // {status, payment_status}
  const [invoicesRefreshKey, setInvoicesRefreshKey] = useState(0);
  const lang = user?.preferred_language === "es" ? "es" : "en";

  // Stripe-return polling: when ?session_id=... is in the URL, poll until payment_status flips
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.error(lang === "es" ? "Pago cancelado" : "Payment cancelled");
      params.delete("cancelled"); setParams(params, { replace: true });
      return;
    }
    if (!sid || !user) return;
    setPayingBanner({ status: "polling" });
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/portal/checkout/status/${sid}`);
        setPayingBanner({ status: data.status, payment_status: data.payment_status });
        if (data.payment_status === "paid") {
          toast.success(lang === "es" ? "¡Pago exitoso!" : "Payment successful!");
          if (data.book_id) setBookId(data.book_id);
          setTab("invoices");
          setInvoicesRefreshKey((k) => k + 1);
          params.delete("session_id"); params.delete("book_id"); setParams(params, { replace: true });
          setTimeout(() => setPayingBanner(null), 5000);
          return;
        }
        if (data.status === "expired") {
          setPayingBanner({ status: "expired" });
          return;
        }
        if (attempts < 7) setTimeout(tick, 2000);
        else setPayingBanner((s) => ({ ...s, status: "timeout" }));
      } catch {
        setPayingBanner({ status: "error" });
      }
    };
    tick();
    // eslint-disable-next-line
  }, [params, user, lang]);

  const TABS = useMemo(() => [
    { key: "pl", icon: BarChart3, label_en: "Profit & Loss", label_es: "Estado de Resultados" },
    { key: "bs", icon: Wallet, label_en: "Balance Sheet", label_es: "Balance General" },
    { key: "cf", icon: TrendingUp, label_en: "Cash Flow", label_es: "Flujo de Efectivo" },
    { key: "invoices", icon: FileText, label_en: "Invoices", label_es: "Facturas" },
    { key: "ar", icon: FileSpreadsheet, label_en: "AR Aging", label_es: "Cuentas por Cobrar" },
  ], []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/portal"); return; }
    (async () => {
      try {
        const { data } = await api.get("/portal/books");
        setBooks(data);
        if (data.length) setBookId(data[0].id);
      } catch { toast.error("Could not load your books"); }
    })();
  }, [user, authLoading, nav]);

  const loadReport = useCallback(async () => {
    if (!bookId || tab === "invoices" || tab === "ar") return;
    setLoading(true); setData(null);
    try {
      let path;
      if (tab === "pl") path = `/portal/books/${bookId}/reports/profit-loss?start=${start}&end=${end}`;
      else if (tab === "bs") path = `/portal/books/${bookId}/reports/balance-sheet?as_of=${asOf}`;
      else if (tab === "cf") path = `/portal/books/${bookId}/reports/cash-flow?start=${start}&end=${end}`;
      const { data: d } = await api.get(path);
      setData(d);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  }, [bookId, tab, start, end, asOf]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const book = books.find((b) => b.id === bookId);

  return (
    <div className="rn3-container py-10" data-testid="portal-reports">
      <button onClick={() => nav("/portal")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="portal-back">
        <ArrowLeft className="w-3 h-3" /> {lang === "es" ? "Volver al Portal" : "Back to Portal"}
      </button>

      {payingBanner && (
        <div className="mb-6 border border-[#C4A252] bg-[#FDFBF7] px-4 py-3 flex items-start gap-3" data-testid="portal-payment-banner">
          {payingBanner.payment_status === "paid" ? (
            <CheckCircle2 className="w-5 h-5 text-[#15803D] mt-0.5" />
          ) : (
            <Loader2 className="w-5 h-5 text-[#C4A252] animate-spin mt-0.5" />
          )}
          <div className="flex-1">
            <div className="font-medium text-sm">
              {payingBanner.payment_status === "paid"
                ? (lang === "es" ? "¡Pago confirmado!" : "Payment confirmed!")
                : payingBanner.status === "expired"
                  ? (lang === "es" ? "Sesión de pago expirada" : "Payment session expired")
                  : payingBanner.status === "timeout"
                    ? (lang === "es" ? "Tiempo de espera agotado — revisa tu email para la confirmación." : "Timed out — please check your email for confirmation.")
                    : payingBanner.status === "error"
                      ? (lang === "es" ? "Error al verificar el pago." : "Error checking payment status.")
                      : (lang === "es" ? "Verificando el pago…" : "Verifying your payment…")}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="rn3-eyebrow"><BookOpen className="w-3 h-3 inline mr-1" /> {lang === "es" ? "Mis Libros" : "My Books"}</div>
          <h1 className="font-serif-display text-4xl mt-2">{lang === "es" ? "Reportes Financieros" : "Financial Reports"}</h1>
        </div>
        {books.length > 0 && (
          <div className="flex items-end gap-3">
            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Libro" : "Book"}</label>
              <select value={bookId} onChange={(e) => setBookId(e.target.value)} className="rn3-input min-w-[240px]" data-testid="portal-book-select">
                {books.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {(tab === "pl" || tab === "cf") && (
              <>
                <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Desde" : "Start"}</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rn3-input" data-testid="portal-start" /></div>
                <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Hasta" : "End"}</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rn3-input" data-testid="portal-end" /></div>
              </>
            )}
            {tab === "bs" && (
              <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "A la fecha" : "As of"}</label><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="rn3-input" data-testid="portal-as-of" /></div>
            )}
          </div>
        )}
      </div>

      {books.length === 0 ? (
        <div className="rn3-card text-center py-16">
          <BookOpen className="w-10 h-10 mx-auto text-[#C4A252]" />
          <div className="mt-4 font-serif-display text-2xl">{lang === "es" ? "Aún no tienes libros" : "No books yet"}</div>
          <p className="mt-2 text-sm text-[#737373] max-w-md mx-auto">{lang === "es" ? "Adelis aún no te ha asociado a un libro contable. Contáctanos para empezar." : "Adelis hasn't linked you to a book yet. Get in touch and we'll set you up."}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-[#E5E5E5] mb-6">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2.5 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${active ? "border-[#8B1C22] text-[#8B1C22]" : "border-transparent text-[#737373] hover:text-[#0A0A0A]"}`} data-testid={`portal-tab-${t.key}`}>
                  <Icon className="w-3.5 h-3.5" /> {lang === "es" ? t.label_es : t.label_en}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2">
              {(tab === "pl" || tab === "bs" || tab === "cf") && book && (
                <>
                  <a
                    href={`#`} onClick={(e) => { e.preventDefault(); const path = tab === "bs"
                      ? `/portal/books/${book.id}/reports/balance-sheet.csv?as_of=${asOf}&lang=${lang}`
                      : `/portal/books/${book.id}/reports/${tab === "pl" ? "profit-loss" : "cash-flow"}.csv?start=${start}&end=${end}&lang=${lang}`;
                      downloadAuth(path, `${tab.toUpperCase()}-${book.name}.csv`);
                    }}
                    className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#0A0A0A]"
                    data-testid="portal-csv"
                  >CSV</a>
                  <a
                    href={`#`} onClick={(e) => { e.preventDefault();
                      const path = `/portal/books/${book.id}/reports/financials.xlsx?start=${start}&end=${end}&as_of=${asOf}&lang=${lang}`;
                      downloadAuth(path, `Financials-${book.name}.xlsx`);
                    }}
                    className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#0A0A0A]"
                    data-testid="portal-xlsx"
                  >XLSX</a>
                </>
              )}
            </div>
          </div>

          {loading && <div className="py-20 text-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>}
          {!loading && tab === "pl" && data && <PLView data={data} lang={lang} />}
          {!loading && tab === "bs" && data && <BSView data={data} lang={lang} />}
          {!loading && tab === "cf" && data && <CFView data={data} lang={lang} />}
          {tab === "invoices" && <InvoicesView bookId={bookId} lang={lang} refreshKey={invoicesRefreshKey} />}
          {tab === "ar" && <ARView bookId={bookId} lang={lang} />}
        </>
      )}
    </div>
  );
};

export default ClientReports;
